import { Grid, TextField, useTheme, IconButton, Dialog, DialogContent, Typography } from "@mui/material";
import React, { useEffect, useRef } from "react";
import { useCallback, useState } from "react";
import { DefaultExtensionType, FileIcon } from "react-file-icon";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";
import AlertLineSuggestedNotes from "./AlertLineSuggestedNotes";
import {
  Container,
  DocumentViewerContainer,
  EndAdornmentContainer,
  SendButton,
  StartAdornmentContainer,
} from "./styles";
import { saveDocs } from "./utils";
import { AttachIcon, SendIcon } from "../../assets/icons";
import { useAppSelector } from "../../custom-hooks/redux/redux";
import { AlertLineReportDTO } from "../../models/alert-line-report/alert-line-report-dto";
import { AlertLineReportRef } from "../../models/alert-line-report/alert-line-report-ref";
import { AlertLineReportNoteSTO } from "../../models/alert-line-report-note/alert-line-report-note-sto";
import { AlertLineReviewerRef } from "../../models/alert-line-reviewer/alert-line-reviewer-ref";
import { AlertLineSuggestedNoteDTO } from "../../models/alert-line-suggested-note/alert-line-suggested-note-dto";
import { AlertLineSuggestedNoteRef } from "../../models/alert-line-suggested-note/alert-line-suggested-note-ref";
import { DocumentRef } from "../../models/documents";
import { customFileIconStyles } from "../../reusable-components/DefaultFileIconStyles/default-file-icon-styles";
import DocumentViewer from "../../reusable-components/DocumentViewer/DocumentViewer";
import { IFile } from "../../reusable-components/DocumentViewer/interfaces";
import { AlertLineReportNoteApiService } from "../../services/alert-line/alert-line-report-note-api.service";
import { PublicAlertLineReportNotesApiService } from "../../services/alert-line/public-alert-line-report-notes-api.service";
import { convertBlobToBase64, convertFileToBase64 } from "../../utils/files-utils/convert-file";
import { getFileExtension } from "../../utils/files-utils/get-file-extension";
import { FileIconButton } from "../AttachedFiles/styles";
import DialogActions from "../DialogActions/DialogActions";
import DialogTitle from "../DialogTitle/DialogTitle";
import { handleUploadNewFiles } from "../Document/Document/utils";
import VoiceRecorder from "../VoiceRecorder/VoiceRecorder";

interface CustomFile {
  file: string;
  fileName: string;
  fileType: string;
}

interface Props {
  alertLineReport: AlertLineReportDTO;
  handleUpdateParent: () => void;
  asReviewer?: boolean;
  latestSuggestedNote?: AlertLineSuggestedNoteRef;
}

interface State {
  note: AlertLineReportNoteSTO;
  audioURL: string;
  loading: boolean;
  error: string;
  file: IFile | null;
  viewFile: boolean;
  confirmFileDialogOpen: boolean;
  suggestedNotes: AlertLineSuggestedNoteDTO[];
  loadingSuggestedNotes: boolean;
  customFile: CustomFile;
  vocalMessageFile: IFile | null;
}

const AddAlertLineReportNote = ({
  alertLineReport,
  handleUpdateParent,
  asReviewer = false,
  latestSuggestedNote,
}: Props) => {
  const tRP = "components.alert_line_notes";
  const { t } = useTranslation();
  const theme = useTheme();

  const { currentUserData } = useAppSelector((state) => state.currentUserReducer);

  const reviewers: AlertLineReviewerRef[] =
    alertLineReport.alertLineURL?.reviewers.filter((reviewer) => reviewer.user?.id === currentUserData?.user?.id) ?? [];

  const DEFAULT_ITEM: AlertLineReportNoteSTO = {
    isDeleted: false,
    id: "",
    message: "",
    vocalMessage: "",
    alertLineReport: alertLineReport as AlertLineReportRef,
    document: null,
    asReporter: !asReviewer,
    asManager: (asReviewer && !!reviewers.find((_reviewer) => _reviewer.asManager)) ?? false,
    asCompanyCommitteeMember:
      (asReviewer && !!reviewers.find((_reviewer) => _reviewer.asCompanyCommitteeMember)) ?? false,
    asHR: (asReviewer && !!reviewers.find((_reviewer) => _reviewer.asHR)) ?? false,
    basedOn: null,
  };

  const INITIAL_STATE: State = {
    note: DEFAULT_ITEM,
    audioURL: "",
    loading: false,
    error: "",
    file: null,
    viewFile: false,
    confirmFileDialogOpen: false,
    suggestedNotes: [],
    loadingSuggestedNotes: false,
    customFile: {
      file: "",
      fileName: "",
      fileType: "",
    },
    vocalMessageFile: null,
  };

  const [state, setState] = useState<State>({
    ...INITIAL_STATE,
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  //#region change text
  const handleText = useCallback((message: string) => {
    setState((state) => ({
      ...state,
      note: {
        ...state.note,
        message,
      },
    }));
  }, []);
  //#endregion

  //#region open file picker
  const handleAddFile = useCallback(() => {
    if (fileInputRef.current) fileInputRef.current.click();
  }, []);
  //#endregion

  //#region handle file
  const handleFileChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files[0];

    if (file) {
      const base64File = await convertFileToBase64({ file, fileName: file.name ?? "" });

      setState((state) => ({
        ...state,
        customFile: {
          file: base64File,
          fileType: getFileExtension(file.name),
          fileName: file.name,
        } as CustomFile,
        file: {
          file,
          fileName: file.name,
          fileExtension: getFileExtension(file.name),
        } as IFile,
        confirmFileDialogOpen: true,
      }));
    }
  }, []);
  //#endregion

  //#region handle audio
  const handleAudio = useCallback(async (audioURL: string, audioBlob: Blob) => {
    const fileToSave = new File([audioBlob], "vocal_message.wav", { lastModified: Date.now() });

    const base64Audio = await convertBlobToBase64(audioBlob);
    setState((state) => ({
      ...state,
      note: {
        ...state.note,
        vocalMessage: base64Audio,
      },
      audioURL,
      vocalMessageFile: {
        file: fileToSave,
        fileName: "vocal_message.wav",
        fileExtension: "wav",
      } as IFile,
    }));
  }, []);

  useEffect(() => {
    !!state.vocalMessageFile && saveNote();
  }, [state.vocalMessageFile]);
  //#endregion

  //#region save note
  const saveNote = useCallback(async () => {
    setState((state) => ({ ...state, loading: true }));
    const serviceApi = !asReviewer ? new PublicAlertLineReportNotesApiService() : new AlertLineReportNoteApiService();

    try {
      let vocalMessageDoc: DocumentRef | null = null;
      let docToSave: DocumentRef | null = null;

      if (state.vocalMessageFile) {
        const res = await saveDocs();

        if (res.response.error) {
          return;
        }

        await Promise.all([handleUploadNewFiles(res.docOne, [state.vocalMessageFile] as any, null, null, true)]);

        vocalMessageDoc = res.docOne;
      }

      if (state.file) {
        const res = await saveDocs();

        if (res.response.error) {
          return;
        }

        await Promise.all([handleUploadNewFiles(res.docOne, [state.file] as any, null, null, true)]);

        docToSave = res.docOne;
      }

      const response = await serviceApi.saveItems([
        {
          ...state.note,
          id: uuid(),
          vocalMessage: vocalMessageDoc,
          document: docToSave,
        },
      ]);
      if (response.error) {
        setState((state) => ({ ...state, error: response.error }));
      } else {
        setState({ ...INITIAL_STATE });
        handleUpdateParent && handleUpdateParent();
      }
    } catch (error) {
      setState((state) => ({ ...state, error }));
    } finally {
      setState((state) => ({ ...state, loading: false }));
    }
  }, [state.note, INITIAL_STATE, state.vocalMessageFile]);
  //#endregion

  //#region deleted file
  const handleDeleteFile = useCallback(() => {
    const confirm = window.confirm("Are you sue you want to delete this file");
    if (confirm) {
      setState((state) => ({
        ...state,
        file: null,
        note: {
          ...state.note,
          file: "",
          fileName: "",
          fileType: "",
        },
      }));
    }
  }, []);
  //#endregion

  //#region pick suggested note
  const pickSuggestedNote = useCallback((suggestedNote: AlertLineSuggestedNoteRef) => {
    setState((state) => ({
      ...state,
      note: {
        ...state.note,
        message: suggestedNote.note,
        basedOn: suggestedNote,
      },
    }));
  }, []);
  //#endregion

  return (
    <>
      <Container>
        <TextField
          label={`${t(`${tRP}.add_note`)}`}
          value={state.note.message}
          onChange={(e) => handleText(e.target.value)}
          error={!!state.error}
          helperText={state.error}
          multiline
          minRows={3}
          InputProps={{
            startAdornment: (
              <StartAdornmentContainer>
                {asReviewer && (
                  <AlertLineSuggestedNotes
                    handleChangeSuggestedNote={pickSuggestedNote}
                    latestSuggestedNote={latestSuggestedNote}
                    alertLineURL={alertLineReport.alertLineURL}
                    reportLanguage={alertLineReport.language}
                  />
                )}
              </StartAdornmentContainer>
            ),
            endAdornment: (
              <EndAdornmentContainer>
                {!state.note.vocalMessage && (
                  <VoiceRecorder
                    handleAudio={handleAudio}
                    onlyIcon
                    customButtonStyles={{
                      backgroundColor: "transparent",
                      boxShadow: "none",
                      color: theme.palette.primary.main,
                      fontSize: "24px",
                      padding: "0px",
                    }}
                    customSaveName={"Send"}
                  />
                )}
                {!state.customFile.file && (
                  <>
                    <IconButton
                      onClick={handleAddFile}
                      title={`${t(`${tRP}.upload_file`)}`}
                    >
                      <AttachIcon
                        color={theme.palette.primary.main}
                        width="35px"
                        height="35px"
                      />
                    </IconButton>
                    <input
                      type="file"
                      ref={fileInputRef}
                      style={{ display: "none" }} // Hide the actual file input element
                      onChange={handleFileChange} // Handle file selection
                    />
                  </>
                )}
              </EndAdornmentContainer>
            ),
          }}
        />

        <SendButton
          onClick={saveNote}
          disabled={state.note.message.trim().length === 0}
        >
          <SendIcon
            color={state.note.message.trim().length === 0 ? theme.palette.secondary.light : theme.palette.primary.main}
            width="25px"
            height="25px"
          />
        </SendButton>
      </Container>

      <DocumentViewerContainer open={state.viewFile}>
        <DialogTitle
          title=""
          handleClose={() => setState((state) => ({ ...state, viewFile: false }))}
        />
        <Grid>
          {state.viewFile && state.file && (
            <DocumentViewer
              file={state.file}
              withFooter
            />
          )}
        </Grid>
      </DocumentViewerContainer>

      <Dialog
        open={state.confirmFileDialogOpen && !!state.file}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          title={`${t(`${tRP}.send_file`)}`}
          handleClose={() => {}}
        />
        <DialogContent>
          {state.file && (
            <>
              <Grid style={{ display: "flex", alignItems: "center", gap: "15px" }}>
                <Typography> {`${t(`${tRP}.you_have_uploaded_the_following_file`)}`} : </Typography>
                <FileIconButton onClick={() => setState((state) => ({ ...state, viewFile: true }))}>
                  <FileIcon
                    extension={state.customFile.fileType}
                    {...customFileIconStyles[state.customFile.fileType as DefaultExtensionType]}
                    radius={3}
                  />
                </FileIconButton>
              </Grid>

              <Typography>{`${t(`${tRP}.are_you_sure_you_want_to_proceed`)}`} </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions
          handleCancel={handleDeleteFile}
          handleSave={saveNote}
          customSaveName={`${t(`${tRP}.yes_send`)}`}
        />
      </Dialog>
    </>
  );
};
export default AddAlertLineReportNote;
